
import { elem, styleInstaller } from "./chrome";

/** The whole sheet, exactly as `Standing` delivered it. */
export interface CharacterModel {
  readonly level: number;
  readonly exp: number;
  readonly expToNext: number;
  readonly maxHp: number;
  readonly attackMin: number;
  readonly attackMax: number;
  readonly defence: number;
  readonly str: number;
  readonly dex: number;
  readonly int: number;
  readonly con: number;
  readonly points: number;
  readonly attackSpeed: number;
  readonly moveSpeed: number;
  readonly castingSpeed: number;
  readonly magicAttackMin: number;
  readonly magicAttackMax: number;
  readonly magicDefence: number;
  readonly evadeRate: number;
  /** POINT_STAT_RESET_COUNT: how many points may still be taken back out
   *  (`uicharacter.py:729-731`). Absent reads as none. */
  readonly statResetCount?: number;
}

/** The spirit pool. Separate because it arrives on its own message, on its own
 *  clock -- it regenerates every three seconds and the rest of the sheet does
 *  not. */
export interface SpiritModel {
  readonly sp: number;
  readonly maxSp: number;
}

export interface CharacterActions {
  /** Put one point into a statistic. The argument is a `Stat` wire value. */
  onSpend?: (stat: number) => void;
  /** Take one point back out of a statistic. The argument is a `Stat` wire value. */
  onReclaim?: (stat: number) => void;
  /** Ask for a player-versus-player mode. The argument is a `PvpMode`. */
  onPvpMode?: (mode: number) => void;
}

/** Wire values, mirrored from `server/stats.py`'s `Stat`. */
export const STAT_STR = 0;
export const STAT_DEX = 1;
export const STAT_INT = 2;
export const STAT_CON = 3;

/** Wire values, mirrored from `server/pvp.py`'s `PvpMode`. */
/** The chat command a status page minus button sends (`uicharacter.py:89-94`). */
export function reclaimCommand(stat: number): string {
  const word = { [STAT_CON]: "ht", [STAT_INT]: "iq", [STAT_STR]: "st", [STAT_DEX]: "dx" }[stat];
  return `/stat- ${word ?? ""}`;
}

/** `STAT_MINUS_DESCRIPTION`, the minus button's tooltip over the reclaims left
 *  (`localeinfo.py:614-619`). */
const RECLAIM_TITLES: Readonly<Record<number, string>> = {
  [STAT_CON]: "Vitality Adjustment (Remaining chance: %d)", // locale_game.txt:517
  [STAT_DEX]: "Dexterity Adjustment (Remaining Chance: %d)", // locale_game.txt:518
  [STAT_INT]: "Intelligence Adjustment (Rest of Chance: %d)", // locale_game.txt:519
  [STAT_STR]: "Strength Adjustment (Rest of Chance: %d)", // locale_game.txt:520
};

export const PVP_PEACE = 0;
export const PVP_REVENGE = 1;
export const PVP_FREE = 2;
export const PVP_PROTECT = 3;
export const PVP_GUILD = 4;

/** The four in the order `Status_Standard` stacks them -- HTH, INT, STR, DEX
 *  (`characterwindow.py:337-374`) -- which is the order `label_std_item1.sub`
 *  prints VIT/INT/STR/DEX in. */
export const STAT_ROWS: readonly {
  readonly stat: number;
  readonly label: string;
  readonly effect: string;
}[] = [
  { stat: STAT_CON, label: "Vitality", effect: "Increases HP and defence" },
  { stat: STAT_INT, label: "Intelligence", effect: "Increases SP, magic attack and defence" },
  { stat: STAT_STR, label: "Strength", effect: "Increases attack value and damage" },
  { stat: STAT_DEX, label: "Dexterity", effect: "Increases attack and evasion" },
];

export const NO_CHARACTER: CharacterModel = {
  level: 1,
  exp: 0,
  expToNext: 0,
  maxHp: 0,
  attackMin: 0,
  attackMax: 0,
  defence: 0,
  str: 0,
  dex: 0,
  int: 0,
  con: 0,
  points: 0,
  attackSpeed: 0,
  moveSpeed: 0,
  castingSpeed: 0,
  magicAttackMin: 0,
  magicAttackMax: 0,
  magicDefence: 0,
  evadeRate: 0,
};

export const NO_SPIRIT: SpiritModel = { sp: 0, maxSp: 0 };

/** `d:/ymir work/ui/`, where `characterwindow.py` gets the status page's art. */
const WINDOW_ART = "/ui/textures/game/windows";
const LOCALE_ART = "/ui/textures/locale/windows";
const PUBLIC_ART = "/ui/textures/public";

/** `Status_Standard`'s right column -- HP, SP, ATT, DEF
 *  (`characterwindow.py:383-419`), which `label_std_item2.sub` prints. */
const STANDARD_ROWS = ["HP", "SP", "Attack", "Defence"] as const;

/** `Status_Extent`'s two columns -- MOV, ASPD, CSPD and MATT, MDEF, ER
 *  (`characterwindow.py:438-495`), which `label_ext_item1.sub` and
 *  `label_ext_item2.sub` print; `Standing` states all six. */
const EXTENT_ROWS = ["Moving Speed", "Attack Speed", "Casting Speed"] as const;
const MAGIC_ROWS = ["Magic Damage", "Magic Defence", "Evading"] as const;

/** A statistic's current value, by wire index. Returns zero for one this build
 *  has never heard of, rather than throwing inside a repaint. */
export function statValue(model: CharacterModel, stat: number): number {
  switch (stat) {
    case STAT_STR:
      return model.str;
    case STAT_DEX:
      return model.dex;
    case STAT_INT:
      return model.int;
    case STAT_CON:
      return model.con;
    default:
      return 0;
  }
}

/** How full the experience bar is, 0 to 1. Zero `expToNext` is the level cap,
 *  which draws full rather than empty -- an empty bar at the cap reads as a
 *  character who lost their progress. */
export function expFraction(model: CharacterModel): number {
  if (model.expToNext <= 0) return 1;
  return Math.max(0, Math.min(1, model.exp / model.expToNext));
}

/** What the damage line says. A character with no weapon has no spread, and
 *  writing "14 - 14" for that would look like a rendering bug. */
export function damageLabel(model: CharacterModel): string {
  if (model.attackMax <= model.attackMin) return String(model.attackMin);
  return `${model.attackMin} - ${model.attackMax}`;
}

/** Whether a mode means "I can be fought". Anything the client does not
 *  recognise reads as peace, which is the safe direction: a mode drawn as
 *  fightable that is not merely surprises; the reverse gets somebody killed. */
export function fightable(mode: number): boolean {
  return mode !== PVP_PEACE && mode >= PVP_REVENGE && mode <= PVP_GUILD;
}

// The shipped option words, not house paraphrases: `PVP_OPTION_KILL/NORMAL/
// PROTECT/REVENGE` (`locale_game.txt:379-382`) and `OPTION_PVPMODE_GUILD`
// (`locale_interface.txt:239`), the fifth mode that only the interface file
// names.
function pvpLabel(mode: number): string {
  switch (mode) {
    case PVP_REVENGE: return "Hostile";
    case PVP_FREE: return "Free";
    case PVP_PROTECT: return "Protection";
    case PVP_GUILD: return "Guild";
    default: return "Peace";
  }
}

const HINT = "A spent point is permanent · Esc to close";

const CSS = `
/* Centred, like the atlas on M, rather than a flow child of #overlay.
   #overlay is a zero-width column down the left edge, and stacking there is
   what the vitals panel correctly does -- but that panel is six lines tall and
   this one is nearly four hundred pixels. Placed in the column it ran straight
   through the chat help text and the chat input, which sit at the bottom of the
   same edge: legible against the sky, unreadable against a wall. The two free
   corners are not free either -- top-right is the minimap radar, bottom-right
   is the bag -- so the honest answer for a panel this size is the one the atlas
   already uses: the middle of the screen, over everything, closed with the same
   key that opened it. */
.sheet {
  position: fixed;
  left: 50%;
  top: 50%;
  transform: translate(-50%, -50%);
  max-height: calc(100vh - 48px);
  overflow-y: auto;
  z-index: 5;
  width: 280px;
  max-width: calc(100vw - 32px);
  padding: 10px;
  border-radius: 10px;
  background: rgba(12, 14, 18, 0.86);
  border: 1px solid rgba(255, 236, 170, 0.22);
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.45);
  pointer-events: auto;
  font: 12px/1.4 system-ui, sans-serif;
  color: #d8dee6;
}
/* Inside CharacterWindow the sheet is the STATUS page, not a floating card
   (characterwindow.py:196-205): fill the page between titlebar and tabs, top
   edge first, so it opens on the face and level rows (:274-308). */
.charwin-page .sheet {
  position: absolute;
  inset: 0;
  transform: none;
  width: auto;
  max-width: none;
  max-height: none;
  z-index: auto;
  padding: 0;
  overflow: hidden;
  border-radius: 0;
  background: transparent;
  border: 0;
  box-shadow: none;
}
.charwin-page .sheet-header,
.charwin-page .sheet-hint,
.charwin-page .sheet-bar,
.charwin-page .sheet-pvp,
.charwin-page .sheet-stat-effect { display: none; }
.charwin-page .sheet-stat-label { white-space: nowrap; }
/* Every block at its board coordinate (characterwindow.py): the page is the
   whole board above the tabs, so nothing falls below the fold. */
.charwin-page .sheet-face { position: absolute; left: 7px; top: 7px; } /* Face_Slot :308 */
.charwin-page .sheet-name { /* Character_Name_Slot (153,34) :247-250 */
  position: absolute; left: 153px; top: 34px; width: 90px; height: 18px; margin: 0;
  font: 11px/18px Tahoma, "Segoe UI", sans-serif; text-align: center;
  background: url("/ui/parameter_slot_03.png") no-repeat left top;
}
.charwin-page .sheet-plates { margin: 0; }
.charwin-page .sheet-plate { position: absolute; top: 61px; } /* Status_Header (3,31) + (x,30) :272-296 */
.charwin-page .sheet-plate-level { left: 12px; }
.charwin-page .sheet-plate-exp { left: 56px; }
.charwin-page .sheet-plate-need { left: 153px; }
.charwin-page .sheet-band { position: absolute; margin: 0; }
.charwin-page .sheet-band-std { left: 16px; top: 109px; } /* (3,100) + (13,9) :317 */
.charwin-page .sheet-band-ext { left: 16px; top: 229px; } /* (3,221) + (13,8) :431 */
.charwin-page .sheet-points { /* Status_Plus_Label (3,100) + (150,11) :323 */
  position: absolute; left: 153px; top: 111px; width: 75px; height: 14px;
  margin: 0; padding: 0; border: 0; display: block;
}
.charwin-page .sheet-points-label { position: absolute; left: 0; top: 0; }
.charwin-page .sheet-points-value { /* Status_Plus_Value x=62, centred :328 */
  position: absolute; left: 52px; top: 0; width: 20px; text-align: center;
  font: 700 11px/14px Tahoma, "Segoe UI", sans-serif; color: #fff;
}
.charwin-page .sheet-stats { /* ItemList1 (3,100) + (17,31) :333; minus at x 12 :377 */
  position: absolute; left: 12px; top: 131px; width: 100px; height: 92px;
  margin: 0; padding: 0; gap: 0;
  background-position: 8px 0;
}
.charwin-page .sheet-stat { position: relative; height: 23px; display: block; }
.charwin-page .sheet-reclaim { position: absolute; left: 0; top: 4px; } /* HTH_Minus (9,35) :377 */
.charwin-page .sheet-stat-value { /* HTH_Label (50,32), Parameter_Slot_00 :338-342 */
  position: absolute; left: 41px; top: 1px; width: 39px; min-width: 0; height: 18px;
  text-align: center; font: 11px/18px Tahoma, "Segoe UI", sans-serif;
  background: url("/ui/parameter_slot_00.png") no-repeat left top;
}
.charwin-page .sheet-spend { /* HTH_Plus at (41,3) inside the label :343 */
  position: absolute; left: 82px; top: 4px; width: 13px; height: 13px;
  border: 0; border-radius: 0; font-size: 0; color: transparent;
  background: url("${WINDOW_ART}/btn_plus_up.png") center / 13px 13px no-repeat;
}
.charwin-page .sheet-spend:hover { background-image: url("${WINDOW_ART}/btn_plus_over.png"); }
.charwin-page .sheet-spend:active { background-image: url("${WINDOW_ART}/btn_plus_down.png"); }
.charwin-page .sheet-derived { display: block; margin: 0; padding: 0; border: 0; }
.charwin-page .sheet-derived-column {
  position: absolute; display: block; margin: 0; padding-top: 2px;
}
.charwin-page .sheet-derived-column dt { display: none; }
.charwin-page .sheet-derived-column dd { margin: 0 0 5px; }
.charwin-page .sheet-derived-std { /* ItemList2 (100,30), values at x 145 :334, :386-417 */
  left: 103px; top: 130px; width: 135px; height: 91px; padding-left: 45px;
}
.charwin-page .sheet-derived-std dd { width: 90px; background-image: url("/ui/parameter_slot_03.png"); }
.charwin-page .sheet-derived-ext { /* ItemList1 (11,31), values at x 66 :434, :439 */
  left: 14px; top: 252px; width: 107px; height: 65px; padding-left: 55px;
}
.charwin-page .sheet-derived-magic { /* ItemList2 (128,32), values at x 183 :435, :469 */
  left: 131px; top: 253px; width: 107px; height: 65px; padding-left: 55px; padding-top: 1px;
}
.charwin-page .sheet-derived-ext dd,
.charwin-page .sheet-derived-magic dd { width: 52px; }
/* Face_Image at (11,11) with Face_Slot over it at (7,7), 43x43 inside 53x53
   (characterwindow.py:307-308). */
.sheet-face {
  position: relative;
  width: 53px;
  height: 53px;
  background: url("${WINDOW_ART}/box_face.png") no-repeat left top;
  image-rendering: pixelated;
}
.sheet-face-art {
  position: absolute;
  left: 4px;
  top: 4px;
  width: 43px;
  height: 43px;
  background: url("${WINDOW_ART}/face_warrior.png") no-repeat left top;
}
/* Status_Lv (37x42), Status_CurExp and Status_RestExp (90x42): the raster
   prints the word, the value is centred in the slot under it
   (characterwindow.py:277-302). */
.sheet-plates { display: flex; gap: 4px; margin: 6px 0; }
.sheet-plate {
  position: relative;
  height: 42px;
  background: no-repeat left top;
  image-rendering: pixelated;
}
.sheet-plate > span {
  position: absolute;
  inset: 20px 0 0;
  display: block;
  text-align: center;
  font: 700 11px/22px Tahoma, "Segoe UI", sans-serif;
  color: #fff;
  font-variant-numeric: tabular-nums;
  overflow: hidden;
}
.sheet-plate-level { width: 37px; background-image: url("${LOCALE_ART}/label_level.png"); }
.sheet-plate-exp { width: 90px; background-image: url("${LOCALE_ART}/label_cur_exp.png"); }
.sheet-plate-need { width: 90px; background-image: url("${LOCALE_ART}/label_last_exp.png"); }
/* Character_Bar_01_Text and Status_Extent_Label, each one 113x15 raster over
   a horizontalbar (characterwindow.py:316-317, :438). */
.sheet-band {
  width: 113px;
  height: 15px;
  margin: 8px 0 2px;
  font-size: 0;
  color: transparent;
  background: no-repeat left top;
  image-rendering: pixelated;
}
.sheet-band-std { background-image: url("${LOCALE_ART}/label_std.png"); }
.sheet-band-ext { background-image: url("${LOCALE_ART}/label_ext.png"); }
/* Status_Plus_Label, 75x14, with Status_Plus_Value printed at x=62
   (characterwindow.py:320-329). */
.sheet-points-label {
  width: 75px;
  height: 14px;
  font-size: 0;
  color: transparent;
  background: url("${LOCALE_ART}/label_uppt.png") no-repeat left top;
  image-rendering: pixelated;
}
/* Status_Standard_ItemList1, 36x91 over four 23px rows
   (characterwindow.py:333, :337-374). */
.sheet-stats {
  padding-left: 40px;
  background: url("${LOCALE_ART}/label_std_item1.png") no-repeat left top;
  image-rendering: pixelated;
}
.sheet-stat { height: 23px; }
.sheet-stat-label { font-size: 0; color: transparent; }
/* Each value sits in Parameter_Slot_01, the 52x18 plate every extended row
   and every derived row draws under its number (characterwindow.py:6). */
.sheet-derived { display: flex; gap: 8px; flex-wrap: wrap; }
.sheet-derived-column {
  display: grid;
  grid-template-columns: auto 52px;
  align-content: start;
  gap: 5px 4px;
  margin: 0;
  background: no-repeat left top;
  image-rendering: pixelated;
}
.sheet-derived-column dt { font-size: 0; color: transparent; }
.sheet-derived-column dd {
  margin: 0;
  height: 18px;
  text-align: center;
  font: 11px/18px Tahoma, "Segoe UI", sans-serif;
  font-variant-numeric: tabular-nums;
  background: url("${PUBLIC_ART}/parameter_slot_01.png") no-repeat left top;
}
.sheet-derived-std {
  padding-left: 50px;
  background-image: url("${LOCALE_ART}/label_std_item2.png");
}
.sheet-derived-ext {
  padding-left: 57px;
  background-image: url("${LOCALE_ART}/label_ext_item1.png");
}
.sheet-derived-magic {
  padding-left: 57px;
  background-image: url("${LOCALE_ART}/label_ext_item2.png");
}
.sheet-header {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  margin-bottom: 8px;
}
.sheet-title {
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  font-size: 11px;
  color: #9aa7b4;
}
.sheet-level { color: #ffe9a8; font-weight: 600; font-variant-numeric: tabular-nums; }
.sheet-name {
  margin: -4px 0 8px;
  font-size: 13px;
  font-weight: 600;
  color: #f2f5f8;
}
.sheet-bar {
  height: 5px;
  border-radius: 3px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
  margin-bottom: 4px;
}
.sheet-bar-fill { height: 100%; background: #6fa8dc; transition: width 200ms ease; }
.sheet-exp {
  font-size: 10px;
  color: #6f7a86;
  font-variant-numeric: tabular-nums;
  margin-bottom: 8px;
}
.sheet-points {
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 4px 0;
  margin-bottom: 4px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.sheet-points-value { color: #ffe9a8; font-weight: 600; font-variant-numeric: tabular-nums; }
.sheet-stats { display: flex; flex-direction: column; gap: 2px; margin: 6px 0 8px; }
.sheet-stat { display: flex; align-items: center; gap: 6px; }
.sheet-stat-label { flex: 1; }
.sheet-stat-effect { font-size: 10px; color: #6f7a86; }
.sheet-stat-value {
  min-width: 24px;
  text-align: right;
  font-variant-numeric: tabular-nums;
  color: #f2f5f8;
  font-weight: 600;
}
.sheet-spend {
  width: 20px;
  height: 20px;
  padding: 0;
  border-radius: 4px;
  border: 1px solid rgba(255, 236, 170, 0.4);
  background: rgba(255, 236, 170, 0.12);
  color: #ffe9a8;
  font: inherit;
  font-weight: 700;
  line-height: 1;
  cursor: pointer;
}
.sheet-spend:hover { background: rgba(255, 236, 170, 0.28); }
/* HTH_Minus..DEX_Minus: 13x13 btn_minus_*.sub left of each statistic
   (characterwindow.py:377-380). */
.sheet-reclaim {
  flex: 0 0 auto;
  width: 13px;
  height: 13px;
  padding: 0;
  border: 0;
  background: url("${WINDOW_ART}/btn_minus_up.png") center / 13px 13px no-repeat;
  cursor: pointer;
}
.sheet-reclaim:hover { background-image: url("${WINDOW_ART}/btn_minus_over.png"); }
.sheet-reclaim:active { background-image: url("${WINDOW_ART}/btn_minus_down.png"); }
.sheet-derived {
  display: grid;
  grid-template-columns: auto auto;
  gap: 2px 10px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.sheet-derived dt { color: #9aa7b4; }
.sheet-derived dd { margin: 0; text-align: right; font-variant-numeric: tabular-nums; }
.sheet-pvp {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  margin-top: 8px;
  padding-top: 8px;
  border-top: 1px solid rgba(255, 255, 255, 0.08);
}
.sheet-pvp-state { color: #9aa7b4; }
.sheet-pvp-free .sheet-pvp-state { color: #e08a7a; font-weight: 600; }
.sheet-pvp button, .sheet-pvp select {
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  font: inherit;
  cursor: pointer;
}
.sheet-pvp button:hover, .sheet-pvp select:hover { background: rgba(255, 255, 255, 0.14); }
.sheet-hint { margin-top: 8px; font-size: 10px; color: #6f7a86; }
@media (max-width: 620px) {
  .sheet { width: min(300px, calc(100vw - 24px)); }
}
`;

const STYLE_ID = "sheet-style";

const installStyle = styleInstaller(STYLE_ID, CSS);

export class CharacterPanel {
  private readonly root: HTMLElement;
  private readonly nameEl: HTMLElement;
  private readonly levelEl: HTMLElement;
  private readonly barFill: HTMLElement;
  private readonly expEl: HTMLElement;
  private readonly needEl: HTMLElement;
  private readonly pointsRow: HTMLElement;
  private readonly pointsEl: HTMLElement;
  private readonly statsEl: HTMLElement;
  private readonly derivedEl: HTMLElement;
  private readonly pvpRow: HTMLElement;
  private readonly pvpStateEl: HTMLElement;
  private readonly pvpSelect: HTMLSelectElement;
  private readonly buttons = new Map<number, HTMLButtonElement>();
  private readonly reclaims = new Map<number, HTMLButtonElement>();
  private readonly values = new Map<number, HTMLElement>();
  private readonly derived = new Map<string, HTMLElement>();

  private model: CharacterModel = NO_CHARACTER;
  private spirit: SpiritModel = NO_SPIRIT;
  private pvpMode = PVP_PEACE;
  /** Current HP, refreshed on its own clock like the spirit pool -- it is not
   *  part of `Standing` (`metin3/net/messages.py`'s `Standing` carries only
   *  `max_hp`), so it arrives the same way `session.selfVitals.hp` already
   *  feeds the taskbar and player gauge. */
  private currentHp = 0;
  private characterName = "";
  private fingerprint = "";
  private open = false;

  constructor(
    container: HTMLElement,
    private readonly actions: CharacterActions = {},
  ) {
    const doc = container.ownerDocument ?? document;
    installStyle(doc);

    this.root = elem(doc, "div", "sheet");
    this.root.hidden = true;

    const header = elem(doc, "div", "sheet-header");
    const title = elem(doc, "span", "sheet-title", "Character");
    header.append(title);

    // `Face_Image` under `Face_Slot` (`characterwindow.py:307-308`). The
    // layout's own portrait is the warrior plate; `RefreshCharacter` swaps in
    // `icon/face/<race>.tga` (`uicharacter.py:1094-1097`), which this build
    // has not imported, so the window keeps what the window loads with.
    const face = elem(doc, "div", "sheet-face");
    face.setAttribute("aria-hidden", "true");
    face.append(elem(doc, "div", "sheet-face-art"));

    // `Status_Lv`, `Status_CurExp` and `Status_RestExp`, each one raster with
    // the value printed in its own slot (`characterwindow.py:274-302`).
    const plates = elem(doc, "div", "sheet-plates");
    this.levelEl = elem(doc, "span", "sheet-level", "1");
    this.expEl = elem(doc, "span", "sheet-exp", "0");
    this.needEl = elem(doc, "span", "sheet-need", "0");
    plates.append(
      this.plate(doc, "sheet-plate-level", "Level", this.levelEl),
      this.plate(doc, "sheet-plate-exp", "EXP", this.expEl),
      this.plate(doc, "sheet-plate-need", "Necessary EXP", this.needEl),
    );

    // `Character_Name` (`uicharacter.py:169-170,1062-1065`): the status page's
    // own name field, hidden until the server has actually named someone --
    // matching the sheet's existing "coherent before the first frame" rule
    // rather than showing a placeholder that looks like a real name.
    this.nameEl = elem(doc, "div", "sheet-name");
    this.nameEl.hidden = true;

    const bar = elem(doc, "div", "sheet-bar");
    this.barFill = elem(doc, "div", "sheet-bar-fill");
    bar.append(this.barFill);

    // `Character_Bar_01` with `label_std.sub` over it, and `Status_Extent_Bar`
    // with `label_ext.sub` (`characterwindow.py:315-316`, `:437-438`).
    const standardBand = elem(doc, "div", "sheet-band sheet-band-std", "Character Status");
    const extentBand = elem(doc, "div", "sheet-band sheet-band-ext", "Attributes");

    this.pointsRow = elem(doc, "div", "sheet-points");
    // `Status_Plus_Label` is `label_uppt.sub` with `Status_Plus_Value` printed
    // in it (`characterwindow.py:320-329`); the raster is the word.
    const pointsLabel = elem(doc, "span", "sheet-points-label", "available");
    this.pointsEl = elem(doc, "span", "sheet-points-value", "0");
    this.pointsRow.append(pointsLabel, this.pointsEl);

    this.statsEl = elem(doc, "div", "sheet-stats");
    for (const { stat, label, effect } of STAT_ROWS) {
      const row = elem(doc, "div", "sheet-stat");

      const minus = doc.createElement("button");
      minus.type = "button";
      minus.className = "sheet-reclaim";
      minus.dataset.stat = String(stat);
      minus.hidden = true;
      minus.addEventListener("click", () => this.actions.onReclaim?.(stat)); // uicharacter.py:431-434
      this.reclaims.set(stat, minus);

      const name = elem(doc, "span", "sheet-stat-label", label);
      name.title = effect;

      const hint = elem(doc, "span", "sheet-stat-effect", effect);

      const value = elem(doc, "span", "sheet-stat-value", "0");
      this.values.set(stat, value);

      const plus = doc.createElement("button");
      plus.type = "button";
      plus.className = "sheet-spend";
      plus.textContent = "+";
      plus.title = `Put one point into ${label.toLowerCase()}`;
      plus.dataset.stat = String(stat);
      plus.hidden = true;
      // Nothing local changes. The `Standing` that comes back is the answer,
      // and it comes back even when the server refuses -- see the module note.
      plus.addEventListener("click", () => this.actions.onSpend?.(stat));
      this.buttons.set(stat, plus);

      row.append(minus, name, hint, value, plus);
      this.statsEl.append(row);
    }

    this.derivedEl = elem(doc, "div", "sheet-derived");
    this.derivedEl.append(
      this.column(doc, "sheet-derived-std", STANDARD_ROWS),
      this.column(doc, "sheet-derived-ext", EXTENT_ROWS),
      this.column(doc, "sheet-derived-magic", MAGIC_ROWS),
    );

    this.pvpRow = elem(doc, "div", "sheet-pvp");
    this.pvpStateEl = elem(doc, "span", "sheet-pvp-state");
    this.pvpSelect = elem(doc, "select", "sheet-pvp-select");
    for (const mode of [PVP_PEACE, PVP_REVENGE, PVP_FREE, PVP_PROTECT, PVP_GUILD]) {
      const option = doc.createElement("option");
      option.value = String(mode);
      option.textContent = pvpLabel(mode);
      this.pvpSelect.append(option);
    }
    this.pvpSelect.addEventListener("change", () => {
      const requested = Number(this.pvpSelect.value);
      // Intent only: restore the last server status immediately.  The next
      // status frame moves the selection if, and only if, the server accepted
      // the requested enum.
      this.pvpSelect.value = String(this.pvpMode);
      this.actions.onPvpMode?.(requested);
    });
    this.pvpRow.append(this.pvpStateEl, this.pvpSelect);

    const hint = elem(doc, "div", "sheet-hint", HINT);

    this.root.append(
      header,
      face,
      plates,
      this.nameEl,
      bar,
      standardBand,
      this.pointsRow,
      this.statsEl,
      extentBand,
      this.derivedEl,
      this.pvpRow,
      hint,
    );
    container.append(this.root);
    // `CharacterWindow.OnPressEscapeKey` closes the sheet and consumes Escape
    // (`uicharacter.py:762-764`). Scope it to the visible root so another
    // character sheet mounted in a future overlay cannot close this one.
    this.root.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || !this.showing) return;
      // CharacterWindow owns Escape when this sheet is a tab page.
      if (this.root.closest(".charwin-page") !== null) return;
      event.preventDefault();
      event.stopPropagation();
      this.toggle();
    });
    this.paint();
  }

  /** The character sheet changed: a level, a spent point, a piece of gear. */
  update(model: CharacterModel): void {
    this.model = model;
    this.repaint();
  }

  /** The spirit pool changed. Its own entry point because it has its own
   *  message and arrives every three seconds while the rest does not. */
  updateSpirit(spirit: SpiritModel): void {
    this.spirit = spirit;
    this.repaint();
  }

  /** Current HP changed. Its own entry point for the same reason as the
   *  spirit pool: `Standing` carries `max_hp` only, so this rides whatever
   *  already keeps the taskbar and player gauge current. */
  updateHp(hp: number): void {
    this.currentHp = hp;
    this.repaint();
  }

  /** The server named this character. Its own entry point because the name
   *  is not part of `Standing` either, and can resolve after the sheet is
   *  first drawn (`uicharacter.py:1062-1065` `RefreshCharacter`). */
  updateName(name: string): void {
    this.characterName = name;
    this.repaint();
  }

  /** The server stated a player-versus-player mode. Never called from a click. */
  updatePvpMode(mode: number): void {
    this.pvpMode = mode;
    this.repaint();
  }

  private repaint(): void {
    const print = this.fingerprintOf();
    if (print === this.fingerprint) return;
    this.fingerprint = print;
    this.paint();
  }

  /** One string that changes exactly when the drawn panel must. */
  private fingerprintOf(): string {
    const m = this.model;
    return [
      m.level, m.exp, m.expToNext, m.maxHp, m.attackMin, m.attackMax, m.defence,
      m.str, m.dex, m.int, m.con, m.points, m.attackSpeed, m.moveSpeed,
      m.castingSpeed, m.magicAttackMin, m.magicAttackMax, m.magicDefence, m.evadeRate,
      this.spirit.sp, this.spirit.maxSp, this.pvpMode, this.currentHp,
      this.characterName, m.statResetCount ?? 0,
    ].join(":");
  }

  private paint(): void {
    const m = this.model;
    this.nameEl.textContent = this.characterName;
    this.nameEl.hidden = this.characterName === "";
    this.levelEl.textContent = String(m.level);
    this.barFill.style.width = `${Math.round(expFraction(m) * 100)}%`;
    this.expEl.textContent = m.expToNext > 0 ? String(m.exp) : "maximum level";
    this.needEl.textContent = m.expToNext > 0 ? String(m.expToNext) : "-";

    this.pointsEl.textContent = String(m.points);
    this.pointsRow.hidden = m.points <= 0;

    for (const { stat } of STAT_ROWS) {
      const value = this.values.get(stat);
      if (value) value.textContent = String(statValue(m, stat));
      const button = this.buttons.get(stat);
      // Hidden from the server's own count, exactly as the original hides
      // them. The button is never the check: pressing it sends a request the
      // server refuses by arithmetic (`cmd_general.cpp:735` `if
      // (ch->GetPoint(POINT_STAT) <= 0) return;`).
      if (button) button.hidden = m.points <= 0;
      const minus = this.reclaims.get(stat);
      const reclaims = m.statResetCount ?? 0;
      if (minus) {
        minus.hidden = reclaims <= 0; // uicharacter.py:555-558
        minus.title = (RECLAIM_TITLES[stat] ?? "").replace("%d", String(reclaims)); // uicharacter.py:735
      }
    }

    // `HP_Value` is "cur/max" (`uicharacter.py:501`), not the maximum alone.
    this.setDerived("HP", `${this.currentHp} / ${m.maxHp}`);
    this.setDerived("SP", `${this.spirit.sp} / ${this.spirit.maxSp}`);
    this.setDerived("Attack", damageLabel(m));
    this.setDerived("Defence", String(m.defence));
    this.setDerived("Attack Speed", `${m.attackSpeed}%`);
    this.setDerived("Moving Speed", `${m.moveSpeed}%`);
    this.setDerived("Casting Speed", `${m.castingSpeed}%`);
    // `__GetTotalMagAtkText` prints one number when both ends agree (`uicharacter.py:482-485`).
    this.setDerived("Magic Damage", m.magicAttackMin === m.magicAttackMax
      ? String(m.magicAttackMin) : `${m.magicAttackMin}-${m.magicAttackMax}`);
    this.setDerived("Magic Defence", String(m.magicDefence));
    this.setDerived("Evading", String(m.evadeRate));

    const active = fightable(this.pvpMode);
    this.pvpRow.classList.toggle("sheet-pvp-free", active);
    this.pvpStateEl.textContent = pvpLabel(this.pvpMode);
    this.pvpSelect.value = String(this.pvpMode);
    this.pvpSelect.title = "Ask the server for this PvP mode";
  }

  /** One `Status_*` plate: the raster says the word, the span carries the
   *  value, and the label stays on the element for a reader. */
  private plate(
    doc: Document, cssClass: string, label: string, value: HTMLElement,
  ): HTMLElement {
    const box = elem(doc, "div", `sheet-plate ${cssClass}`);
    box.setAttribute("aria-label", label);
    box.title = label;
    box.append(value);
    return box;
  }

  /** One labelled column of the standard or extended block: the strip prints
   *  every row's word, each `dd` holds its value. */
  private column(
    doc: Document, cssClass: string, keys: readonly string[],
  ): HTMLElement {
    const list = elem(doc, "dl", `sheet-derived-column ${cssClass}`);
    for (const key of keys) {
      const dt = doc.createElement("dt");
      dt.textContent = key;
      const dd = doc.createElement("dd");
      dd.textContent = "-";
      this.derived.set(key, dd);
      list.append(dt, dd);
    }
    return list;
  }

  private setDerived(key: string, text: string): void {
    const node = this.derived.get(key);
    if (node) node.textContent = text;
  }

  toggle(): boolean {
    this.setVisible(!this.open);
    return this.open;
  }

  setVisible(visible: boolean): void {
    this.open = visible;
    this.root.hidden = !visible;
  }

  get showing(): boolean {
    return this.open;
  }

  get element(): HTMLElement {
    return this.root;
  }
}
